/**
 * ResizableImage - Custom Tiptap Node Extension
 * Supports: drag-to-resize, alignment (left/center/right), click-to-select
 */
import React, { useState, useRef, useCallback } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { AlignLeft, AlignCenter, AlignRight, Trash2, Maximize2 } from 'lucide-react';

// ─── Type declarations for custom commands ───
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    resizableImage: {
      setImage: (options: { src: string; alt?: string; title?: string; width?: number; align?: string }) => ReturnType;
    };
  }
}

// ─── React Component for Resizable Image ───
const ResizableImageComponent = (props: any) => {
  const { node, updateAttributes, selected, deleteNode, editor } = props;
  const imgRef = useRef<HTMLImageElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const startState = useRef({ x: 0, y: 0, width: 0 });

  const { src, alt, title, width, align } = node.attrs;

  const onResizeStart = useCallback((e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const imgEl = imgRef.current;
    if (!imgEl) return;

    startState.current = {
      x: e.clientX,
      y: e.clientY,
      width: imgEl.offsetWidth,
    };

    const onMouseMove = (ev: MouseEvent) => {
      const diff = direction.includes('right') 
        ? ev.clientX - startState.current.x 
        : startState.current.x - ev.clientX;
      const newWidth = Math.max(80, startState.current.width + diff);
      updateAttributes({ width: newWidth });
    };

    const onMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [updateAttributes]);

  const alignmentClass = align === 'left' ? 'mr-auto' : align === 'right' ? 'ml-auto' : 'mx-auto';

  return (
    <NodeViewWrapper 
      className="resizable-image-wrapper" 
      data-align={align || 'center'}
      style={{
        display: 'flex',
        justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
        margin: '16px 0',
      }}
    >
      <div 
        className="resizable-image-container"
        style={{ 
          position: 'relative', 
          display: 'inline-block',
          maxWidth: '100%',
        }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          title={title || ''}
          draggable={false}
          style={{
            width: width ? `${width}px` : 'auto',
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '6px',
            display: 'block',
            outline: selected ? '3px solid #6366f1' : 'none',
            outlineOffset: '2px',
            cursor: 'default',
            transition: isResizing ? 'none' : 'outline 0.15s',
          }}
        />

        {/* Resize Handles - visible when selected */}
        {selected && (
          <>
            {/* Top-left handle */}
            <div
              onMouseDown={(e) => onResizeStart(e, 'left')}
              style={{
                position: 'absolute', top: -5, left: -5,
                width: 10, height: 10,
                background: '#6366f1', border: '2px solid white',
                borderRadius: '50%', cursor: 'nw-resize',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                zIndex: 10,
              }}
            />
            {/* Top-right handle */}
            <div
              onMouseDown={(e) => onResizeStart(e, 'right')}
              style={{
                position: 'absolute', top: -5, right: -5,
                width: 10, height: 10,
                background: '#6366f1', border: '2px solid white',
                borderRadius: '50%', cursor: 'ne-resize',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                zIndex: 10,
              }}
            />
            {/* Bottom-left handle */}
            <div
              onMouseDown={(e) => onResizeStart(e, 'left')}
              style={{
                position: 'absolute', bottom: -5, left: -5,
                width: 10, height: 10,
                background: '#6366f1', border: '2px solid white',
                borderRadius: '50%', cursor: 'sw-resize',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                zIndex: 10,
              }}
            />
            {/* Bottom-right handle */}
            <div
              onMouseDown={(e) => onResizeStart(e, 'right')}
              style={{
                position: 'absolute', bottom: -5, right: -5,
                width: 10, height: 10,
                background: '#6366f1', border: '2px solid white',
                borderRadius: '50%', cursor: 'se-resize',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                zIndex: 10,
              }}
            />

            {/* Alignment & Action Toolbar */}
            <div style={{
              position: 'absolute', bottom: -40, left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', gap: 4,
              background: 'white', border: '1px solid #e2e8f0',
              borderRadius: 8, padding: '4px 6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              zIndex: 20,
            }}>
              <button
                onClick={() => updateAttributes({ align: 'left' })}
                title="Align Left"
                style={{
                  padding: '4px 6px', border: 'none', borderRadius: 4,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  background: align === 'left' ? '#eef2ff' : 'transparent',
                  color: align === 'left' ? '#4f46e5' : '#64748b',
                }}
              >
                <AlignLeft size={14} />
              </button>
              <button
                onClick={() => updateAttributes({ align: 'center' })}
                title="Align Center"
                style={{
                  padding: '4px 6px', border: 'none', borderRadius: 4,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  background: align === 'center' || !align ? '#eef2ff' : 'transparent',
                  color: align === 'center' || !align ? '#4f46e5' : '#64748b',
                }}
              >
                <AlignCenter size={14} />
              </button>
              <button
                onClick={() => updateAttributes({ align: 'right' })}
                title="Align Right"
                style={{
                  padding: '4px 6px', border: 'none', borderRadius: 4,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  background: align === 'right' ? '#eef2ff' : 'transparent',
                  color: align === 'right' ? '#4f46e5' : '#64748b',
                }}
              >
                <AlignRight size={14} />
              </button>
              <div style={{ width: 1, background: '#e2e8f0', margin: '0 2px' }} />
              <button
                onClick={() => updateAttributes({ width: null })}
                title="Reset Size"
                style={{
                  padding: '4px 6px', border: 'none', borderRadius: 4,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  background: 'transparent', color: '#64748b',
                }}
              >
                <Maximize2 size={14} />
              </button>
              <button
                onClick={() => deleteNode()}
                title="Delete Image"
                style={{
                  padding: '4px 6px', border: 'none', borderRadius: 4,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  background: 'transparent', color: '#ef4444',
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>

            {/* Width indicator */}
            {width && (
              <div style={{
                position: 'absolute', top: -28, right: 0,
                background: '#1e293b', color: 'white',
                fontSize: 10, fontWeight: 700,
                padding: '2px 8px', borderRadius: 4,
                letterSpacing: '0.05em',
              }}>
                {Math.round(width)}px
              </div>
            )}
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

// ─── Tiptap Node Extension ───
export const ResizableImage = Node.create({
  name: 'resizableImage',

  addOptions() {
    return {
      inline: false,
      allowBase64: true,
      HTMLAttributes: {},
    };
  },

  inline: false,
  group: 'block',
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
      align: { default: 'center' },
    };
  },

  parseHTML() {
    return [{ tag: 'img[src]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },

  addCommands() {
    return {
      setImage: (options: { src: string; alt?: string; title?: string; width?: number; align?: string }) => ({ commands }: { commands: any }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

export default ResizableImage;
